import { FC, useState, useEffect } from "react";

import styles from "./NavigationTopBar.module.scss";
import classNames from "classnames";
import ProjectSelector from "@components/ProjectSelector/ProjectSelector";
import { Language, Project } from "@redux/services/types";
import { Button, ContextMenu, Dialog, Tabs } from "@livingmap/hippo";
import LanguageSwitcher from "@components/LanguageSwitcher/LanguageSwitcher";
import { MenuItem } from "@livingmap/hippo/dist/components/ContextMenu/ContextMenu";
import { ParsedToken } from "screens/FeaturesView/FeaturesView";
import PublishModalContent from "./PublishModalContent/PublishModalContent";
import { useTranslation } from "react-i18next";

interface TabItem {
  text: string;
  url: string;
}
interface Props {
  dataQA: string;
  className?: string;
  projects: Project[];
  onProjectChange: (project: Project) => void;
  activeProject: Project;
  changes?: {
    features: number;
    deletedFeatures: number;
  };
  languages: Language[];
  activeLanguage: string;
  onLanguageChange: (language: Language) => void;
  userProfile?: ParsedToken;
  userMenuItems?: MenuItem[];
  tabItems?: TabItem[];
  onTabChange?: (tab: TabItem) => void;
  onPublishChanges?: () => void;
  initialPath?: string;
}

const NavigationTopBar: FC<Props> = ({
  dataQA,
  className,
  projects,
  onProjectChange,
  activeProject,
  changes,
  languages,
  activeLanguage,
  onLanguageChange,
  userProfile,
  userMenuItems,
  tabItems,
  onTabChange,
  onPublishChanges,
  initialPath,
}) => {
  const selectedItem =
    initialPath && tabItems
      ? tabItems.find((item) => item.url === initialPath)
      : tabItems && tabItems[0];
  const [activeTab, setActiveTab] = useState(selectedItem);
  const [isLanguageSwitcherOpen, setIsLanguageSwitcherOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const { t } = useTranslation();
  let totalChanges = 0;

  if (changes) {
    // Map through all "changes" keys & sum up total changes
    totalChanges = Object.keys(changes)
      .map((key) => changes[key as keyof typeof changes])
      .reduce((previous, current) => previous + current);
  }

  useEffect(() => {
    if (tabItems) {
      const selectedTab = tabItems.find(
        (item) => item.text === activeTab?.text,
      );
      if (!selectedTab) {
        setActiveTab(tabItems[0]);
        onTabChange && onTabChange(tabItems[0]);
      }
    }
  }, [tabItems, onTabChange, activeTab]);

  return (
    <div data-qa={dataQA} className={classNames(styles.container, className)}>
      <ProjectSelector
        dataQA="project-selector"
        projects={projects}
        onProjectSelect={onProjectChange}
        activeProject={activeProject}
      />
      {tabItems && activeTab && (
        <Tabs
          dataQA="map-list-view-selector"
          expanded={false}
          tabItems={tabItems.map((item) => item.text)}
          selected={activeTab.text}
          onChange={(tab) => {
            const selectedTab = tabItems.find((item) => item.text === tab);
            if (selectedTab) {
              setActiveTab(selectedTab);
              onTabChange && onTabChange(selectedTab);
            }
          }}
          underlined={false}
        />
      )}
      <div className={styles.rightControls}>
        {changes !== undefined && onPublishChanges && (
          <>
            <Button
              dataQA="publish-changes-button"
              size="regular"
              style="filled"
              text="Publish"
              icon="file_upload"
              type="primary"
              disabled={totalChanges === 0}
              onClick={() => setIsPublishModalOpen(true)}
            />
            <Dialog
              dataQA="publish-dialog"
              isOpen={isPublishModalOpen}
              onClose={() => setIsPublishModalOpen(false)}
              footer={
                <div className={styles.buttonWrapper}>
                  <Button
                    dataQA="keep-editing-button"
                    size="regular"
                    type="primary"
                    style="outlined"
                    text={t("btn_labels.keep_editing")}
                    onClick={() => setIsPublishModalOpen(false)}
                  />
                  <Button
                    dataQA="publish-button"
                    size="regular"
                    type="primary"
                    style="filled"
                    text={t("btn_labels.publish")}
                    onClick={() => {
                      onPublishChanges && onPublishChanges();
                      setIsPublishModalOpen(false);
                    }}
                  />
                </div>
              }
            >
              <PublishModalContent changes={changes} />
            </Dialog>
          </>
        )}
        <LanguageSwitcher
          dataQA="language-switcher"
          availableLanguages={languages}
          currentLanguage={activeLanguage}
          onLanguageSelect={onLanguageChange}
          headerText="Language"
          onButtonClick={() =>
            setIsLanguageSwitcherOpen(!isLanguageSwitcherOpen)
          }
          open={isLanguageSwitcherOpen}
        />
        {userProfile && (
          <ContextMenu
            dataQA="user-menu"
            className={styles.userMenu}
            size="regular"
            headerText={`${userProfile.name}`}
            subHeaderText="Signed in as"
            username={userProfile.email}
            menuItems={userMenuItems || []}
            style="outlined"
            text=""
            type="primary"
          />
        )}
      </div>
    </div>
  );
};

export default NavigationTopBar;
